/** Producer screen — controls the live OBS overlay with attempt management. */

import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/api/client";
import { useCompetitionStore } from "@/stores/competition";
import { useAuthStore } from "@/stores/auth";
import { RaleySocket } from "@/api/ws";
import { Button } from "@/components/ui/Button";
import { STYLE_LIST } from "@/overlay/styles";
import { useNotifications, NotificationToasts } from "@/hooks/useNotifications";
import type { Division, Heat, HeatRider, LiveState, OverlayStyle, Rider, Score, WSMessage } from "@/types";

export function ProducerPage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const user = useAuthStore((s) => s.user);
  const {
    competition,
    heats,
    riders,
    members,
    divisions,
    liveState,
    loadCompetition,
    loadHeats,
    loadRiders,
    loadMembers,
    loadDivisions,
    loadLiveState,
    setLiveState,
  } = useCompetitionStore();
  const membersRef = useRef(members);
  membersRef.current = members;
  const [heatRiders, setHeatRiders] = useState<HeatRider[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editHeatsOpen, setEditHeatsOpen] = useState(false);
  // Score grid is always visible — no toggle needed
  const [allScores, setAllScores] = useState<Score[]>([]);
  const [finals, setFinals] = useState<Record<string, number | null>>({});
  const { items: notifications, notify: addNotification } = useNotifications(20, 3000);
  const [wsConnected, setWsConnected] = useState(true);

  useEffect(() => {
    if (!competitionId) return;
    Promise.all([
      loadCompetition(competitionId),
      loadHeats(competitionId),
      loadRiders(),
      loadMembers(competitionId),
      loadDivisions(competitionId),
      loadLiveState(competitionId),
    ]).then(() => setLoading(false));
  }, [competitionId, loadCompetition, loadHeats, loadRiders, loadMembers, loadDivisions, loadLiveState]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!competition) return;
    const wsUrl = `${import.meta.env.VITE_WS_URL ?? `ws://${window.location.host}`}/api/obs/${competition.obs_token}/ws`;
    const socket = new RaleySocket(wsUrl);
    socket.connect();

    const unsubStatus = socket.onStatus(setWsConnected);

    const unsub = socket.subscribe((msg: WSMessage) => {
      if (msg.type === "live_state") {
        setLiveState(msg as unknown as LiveState);
        // Refresh heats + current heat riders in case someone changed them
        if (competitionId) {
          loadHeats(competitionId);
          const heatId = (msg as unknown as LiveState).live_heat_id;
          if (heatId) {
            api
              .get<HeatRider[]>(`/api/competitions/${competitionId}/heats/${heatId}/riders`)
              .then(setHeatRiders);
          }
        }
      }
      if (msg.type === "scores_update") {
        setFinals(msg.finals);
        // Re-fetch full scores (with attempt numbers) and diff for notifications
        const heatId = msg.heat_id;
        if (competitionId && heatId) {
          api.get<Score[]>(`/api/competitions/${competitionId}/heats/${heatId}/scores`).then((newScores) => {
            setAllScores((prev) => {
              // Find new/changed scores by comparing
              const prevMap = new Map(prev.map((s) => [`${s.rider_id}:${s.judge_number}:${s.attempt_number}`, s.value]));
              let dinged = false;
              setHeatRiders((hrs) => {
                for (const s of newScores) {
                  const key = `${s.rider_id}:${s.judge_number}:${s.attempt_number}`;
                  if (prevMap.get(key) !== s.value) {
                    const rider = hrs.find((hr) => hr.rider.id === s.rider_id);
                    const judge = membersRef.current.find((m) => m.judge_number === s.judge_number);
                    const judgeName = judge?.judge_name ?? judge?.user?.display_name?.split(" ")[0] ?? `Judge ${s.judge_number}`;
                    const riderName = rider?.rider.name ?? "Unknown";
                    addNotification({
                      text: `scored ${riderName} #${s.attempt_number}`,
                      judgeName,
                      judgeNum: s.judge_number,
                      detail: s.value.toFixed(1),
                      ding: !dinged,
                    });
                    dinged = true;
                  }
                }
                return hrs;
              });
              return newScores;
            });
          });
        }
      }
      if (msg.type === "attempt_update" || msg.type === "rider_attempt_update") {
        setHeatRiders((prev) =>
          prev.map((hr) =>
            hr.rider.id === msg.rider_id
              ? { ...hr, current_attempt: msg.current_attempt }
              : hr,
          ),
        );
      }
    });

    return () => {
      unsub();
      unsubStatus();
      socket.disconnect();
    };
  }, [competition, competitionId, loadHeats, setLiveState]);

  // Load heat riders + scores when a heat is selected, poll riders every 10s
  useEffect(() => {
    const heatId = selectedHeat ?? liveState?.live_heat_id;
    if (!competitionId || !heatId) return;
    setAllScores([]);
    setFinals({});
    const fetchRiders = () =>
      api
        .get<HeatRider[]>(`/api/competitions/${competitionId}/heats/${heatId}/riders`)
        .then(setHeatRiders);
    fetchRiders();
    // Fetch all scores (with attempt numbers) + finals from OBS state
    api.get<Score[]>(`/api/competitions/${competitionId}/heats/${heatId}/scores`).then(setAllScores);
    if (competition?.obs_token) {
      const apiBase = import.meta.env.VITE_API_URL ?? "";
      fetch(`${apiBase}/api/obs/${competition.obs_token}/state`)
        .then((r) => r.json())
        .then((data: { finals: Record<string, number | null> }) => {
          if (data.finals) setFinals(data.finals);
        })
        .catch(() => {});
    }
    const interval = setInterval(fetchRiders, 10_000);
    return () => clearInterval(interval);
  }, [competitionId, selectedHeat, liveState?.live_heat_id]);

  const updateLive = useCallback(
    async (update: Partial<LiveState>) => {
      if (!competitionId) return;
      const result = await api.put<LiveState>(`/api/competitions/${competitionId}/live`, update);
      setLiveState(result);
    },
    [competitionId, setLiveState],
  );

  const goLive = useCallback(
    async (heatId: string) => {
      if (!competitionId) return;
      const result = await api.post<LiveState>(
        `/api/competitions/${competitionId}/live/go-live/${heatId}`,
      );
      setLiveState(result);
    },
    [competitionId, setLiveState],
  );

  const goDark = useCallback(async () => {
    if (!competitionId) return;
    const result = await api.post<LiveState>(`/api/competitions/${competitionId}/live/go-dark`);
    setLiveState(result);
  }, [competitionId, setLiveState]);

  const advanceAttempt = useCallback(
    async (heatId: string, riderId: string) => {
      if (!competitionId) return;
      const result = await api.post<{ current_attempt: number }>(
        `/api/competitions/${competitionId}/heats/${heatId}/riders/${riderId}/attempt-advance`,
      );
      setHeatRiders((prev) =>
        prev.map((hr) =>
          hr.rider.id === riderId
            ? { ...hr, current_attempt: result.current_attempt }
            : hr,
        ),
      );
    },
    [competitionId],
  );

  const revertAttempt = useCallback(
    async (heatId: string, riderId: string) => {
      if (!competitionId) return;
      const result = await api.post<{ current_attempt: number }>(
        `/api/competitions/${competitionId}/heats/${heatId}/riders/${riderId}/attempt-revert`,
      );
      setHeatRiders((prev) =>
        prev.map((hr) =>
          hr.rider.id === riderId
            ? { ...hr, current_attempt: result.current_attempt }
            : hr,
        ),
      );
    },
    [competitionId],
  );

  if (loading || !competition) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  const activeHeatId = selectedHeat ?? liveState?.live_heat_id ?? heats[0]?.id;
  const activeHeat = heats.find((h) => h.id === activeHeatId);

  // Group heats by division for dropdown
  const divisionMap = new Map<string | null, typeof heats>();
  for (const heat of heats) {
    const key = heat.division_id;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(heat);
  }

  return (
    <div>
      {!wsConnected && (
        <div
          style={{
            background: "rgba(239, 68, 68, 0.15)",
            border: "1px solid #ef4444",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>
            Connection lost — scores and updates may be out of date
          </span>
          <button
            className="btn btn-sm"
            style={{ background: "#ef4444", color: "#fff", border: "none", fontSize: 11, whiteSpace: "nowrap" }}
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">{user?.display_name ?? "Producer"}</h1>
          <div className="flex gap-3 items-center" style={{ marginTop: 2 }}>
            <span className="text-sm text-muted">{competition.name}</span>
          </div>
          <div className="flex gap-3 items-center" style={{ marginTop: 4 }}>
            {liveState?.is_live ? (
              <span className="badge badge-live">LIVE</span>
            ) : (
              <span className="badge badge-off">OFF AIR</span>
            )}
            <select
              value={activeHeatId ?? ""}
              onChange={(e) => {
                const heatId = e.target.value;
                if (!heatId) return;
                if (liveState?.is_live) {
                  goLive(heatId);
                }
                setSelectedHeat(heatId);
              }}
              style={{
                minWidth: 180,
                fontFamily: "var(--font-sans)",
                fontSize: 14,
                padding: "6px 12px",
                background: "var(--c-surface)",
                border: "1px solid var(--c-border)",
                borderRadius: 6,
                color: "var(--c-text)",
                cursor: "pointer",
              }}
            >
              {!activeHeatId && <option value="">Select heat…</option>}
              {Array.from(divisionMap.entries()).map(([divisionId, divisionHeats]) => {
                const divName = divisionId
                  ? divisions.find((d) => d.id === divisionId)?.name ?? "Division"
                  : null;
                return divName ? (
                  <optgroup key={divisionId ?? "__none"} label={divName}>
                    {divisionHeats.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  divisionHeats.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))
                );
              })}
            </select>
            <Button variant="secondary" size="sm" onClick={() => setEditHeatsOpen(true)}>
              Edit Heats
            </Button>
            {liveState?.is_live ? (
              <Button variant="danger" size="sm" onClick={goDark}>
                Go Dark
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => activeHeatId && goLive(activeHeatId)}
                disabled={!activeHeatId}
                title={!activeHeatId ? "Create a heat first" : undefined}
              >
                Go Live
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Global overlay controls */}
      <div className="flex gap-3" style={{ marginBottom: 24 }}>
        <Button
          variant={liveState?.show_current_heat !== false ? "primary" : "secondary"}
          size="sm"
          onClick={() => updateLive({ show_current_heat: liveState?.show_current_heat === false })}
        >
          {liveState?.show_current_heat !== false ? "Hide Heat Name" : "Show Heat Name"}
        </Button>
        <Button
          variant={liveState?.show_all_scores ? "primary" : "secondary"}
          size="sm"
          onClick={() => updateLive({ show_all_scores: !liveState?.show_all_scores })}
        >
          {liveState?.show_all_scores ? "Hide Heat Leaderboard" : "Show Heat Leaderboard"}
        </Button>
        <Button
          variant={liveState?.show_heat_scores ? "primary" : "secondary"}
          size="sm"
          onClick={() => updateLive({ show_heat_scores: !liveState?.show_heat_scores })}
        >
          {liveState?.show_heat_scores ? "Hide Heat Riders" : "Show Heat Riders"}
        </Button>
        <Button
          variant={liveState?.show_rider_scores ? "primary" : "secondary"}
          size="sm"
          onClick={() => updateLive({ show_rider_scores: !liveState?.show_rider_scores })}
        >
          {liveState?.show_rider_scores ? "Hide Rider Scores" : "Show Rider Scores"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => updateLive({ focus_rider_id: null, scores_rider_id: null })}
        >
          Clear Focus
        </Button>
      </div>

      {/* Overlay style switcher */}
      <div style={{ marginBottom: 24 }}>
        <div className="field-label" style={{ marginBottom: 8 }}>
          Overlay Style
        </div>
        <div className="flex gap-2">
          {STYLE_LIST.map((s) => (
            <button
              key={s.id}
              className={`btn btn-sm ${liveState?.overlay_style === s.id ? "btn-primary" : "btn-secondary"}`}
              onClick={() => updateLive({ overlay_style: s.id as OverlayStyle })}
              title={s.description}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* Rider control cards */}
      <div className="control-grid">
        {heatRiders.map((hr) => {
          const isFocused = liveState?.focus_rider_id === hr.rider.id;
          const isScoresShown = liveState?.scores_rider_id === hr.rider.id;
          const maxAttempts = activeHeat?.max_attempts ?? 1;

          return (
            <div key={hr.id} className={`rider-control-card ${isFocused ? "focused" : ""}`}>
              <div className="rider-name">{hr.rider.name}</div>
              <div className="rider-meta">
                {hr.rider.nickname && <span>{hr.rider.nickname}</span>}
                {hr.number && <span> #{hr.number}</span>}
              </div>

              {/* Per-attempt average scores (highlight best) */}
              {(() => {
                const riderScores = allScores.filter((s) => s.rider_id === hr.rider.id);

                if (maxAttempts <= 1) {
                  // Single attempt — show individual judge scores
                  const attempt = Math.max(hr.current_attempt, 1);
                  const attemptScores = riderScores.filter((s) => s.attempt_number === attempt);
                  return (
                    <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                      {Array.from({ length: competition.judge_count }, (_, j) => {
                        const score = attemptScores.find((s) => s.judge_number === j + 1);
                        return (
                          <div
                            key={j}
                            title={`Judge ${j + 1}`}
                            style={{
                              background: "var(--c-surface-2, #1e1e1e)",
                              border: "1px solid var(--c-border)",
                              borderRadius: 3,
                              padding: "1px 5px",
                              fontSize: 10,
                              fontFamily: "var(--font-mono, monospace)",
                              color: score ? "var(--c-text)" : "var(--c-text-muted, #666)",
                              minWidth: 24,
                              textAlign: "center",
                            }}
                          >
                            {score ? score.value.toFixed(1) : "-"}
                          </div>
                        );
                      })}
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: "var(--font-mono, monospace)",
                          color: finals[hr.rider.id] != null ? "var(--c-accent)" : "var(--c-text-muted, #666)",
                          marginLeft: 2,
                        }}
                      >
                        {finals[hr.rider.id] != null ? finals[hr.rider.id]!.toFixed(1) : "-"}
                      </div>
                    </div>
                  );
                }

                // Multi-attempt — show average per attempt, highlight the best
                const attemptAvgs: (number | null)[] = [];
                for (let a = 1; a <= maxAttempts; a++) {
                  const scores = riderScores.filter((s) => s.attempt_number === a);
                  if (scores.length === 0) {
                    attemptAvgs.push(null);
                  } else {
                    attemptAvgs.push(scores.reduce((sum, s) => sum + s.value, 0) / scores.length);
                  }
                }
                const validAvgs = attemptAvgs.filter((v): v is number => v !== null);
                const bestAvg = validAvgs.length > 0 ? Math.max(...validAvgs) : null;

                return (
                  <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                    {attemptAvgs.map((avg, i) => {
                      const isBest = avg !== null && bestAvg !== null && avg === bestAvg;
                      return (
                        <div
                          key={i}
                          title={`Attempt ${i + 1} — avg${avg != null ? ` ${avg.toFixed(1)}` : ""}`}
                          style={{
                            background: isBest ? "var(--c-accent)" : "var(--c-surface-2, #1e1e1e)",
                            border: `1px solid ${isBest ? "var(--c-accent)" : "var(--c-border)"}`,
                            borderRadius: 3,
                            padding: "1px 5px",
                            fontSize: 10,
                            fontFamily: "var(--font-mono, monospace)",
                            color: isBest ? "#fff" : avg != null ? "var(--c-text)" : "var(--c-text-muted, #666)",
                            fontWeight: isBest ? 700 : 400,
                            minWidth: 24,
                            textAlign: "center",
                          }}
                        >
                          {avg != null ? avg.toFixed(1) : "-"}
                        </div>
                      );
                    })}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "var(--font-mono, monospace)",
                        color: finals[hr.rider.id] != null ? "var(--c-accent)" : "var(--c-text-muted, #666)",
                        marginLeft: 2,
                      }}
                    >
                      {finals[hr.rider.id] != null ? finals[hr.rider.id]!.toFixed(1) : "-"}
                    </div>
                  </div>
                );
              })()}

              {/* Attempt indicator dots + controls */}
              {maxAttempts > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    marginBottom: 4,
                  }}
                >
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: "2px 6px", fontSize: 11 }}
                    onClick={() => activeHeatId && revertAttempt(activeHeatId, hr.rider.id)}
                    disabled={hr.current_attempt <= 1}
                    title="Revert attempt"
                  >
                    -
                  </button>

                  <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                    {Array.from({ length: maxAttempts }, (_, i) => {
                      const attemptNum = i + 1;
                      const isActive = attemptNum <= hr.current_attempt;
                      const isCurrent = attemptNum === hr.current_attempt;
                      return (
                        <div
                          key={attemptNum}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: isActive ? "var(--c-accent)" : "transparent",
                            border: `1px solid ${isActive ? "var(--c-accent)" : "var(--c-border)"}`,
                            boxShadow: isCurrent ? "0 0 0 2px var(--c-accent)" : "none",
                            opacity: isActive ? 1 : 0.3,
                            transition: "all 0.15s",
                          }}
                          title={`Attempt ${attemptNum}${isCurrent ? " (current)" : ""}`}
                        />
                      );
                    })}
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: "2px 6px", fontSize: 11 }}
                    onClick={() => activeHeatId && advanceAttempt(activeHeatId, hr.rider.id)}
                    disabled={hr.current_attempt >= maxAttempts}
                    title="Advance attempt"
                  >
                    +
                  </button>

                  <span
                    className="text-mono text-muted"
                    style={{ fontSize: 10, marginLeft: 4 }}
                  >
                    {hr.current_attempt}/{maxAttempts}
                  </span>
                </div>
              )}

              <div className="rider-actions">
                <Button
                  variant={isFocused ? "primary" : "ghost"}
                  size="sm"
                  onClick={() =>
                    updateLive({
                      focus_rider_id: isFocused ? null : hr.rider.id,
                    })
                  }
                >
                  {isFocused ? "Unfocus" : "Focus"}
                </Button>
                <Button
                  variant={isScoresShown ? "primary" : "ghost"}
                  size="sm"
                  onClick={() =>
                    updateLive({
                      scores_rider_id: isScoresShown ? null : hr.rider.id,
                    })
                  }
                >
                  {isScoresShown ? "Hide Scores" : "Show Scores"}
                </Button>
              </div>

              {/* Score grid: judges (rows) x attempts (cols) — always visible */}
              {(() => {
                const riderScores = allScores.filter((s) => s.rider_id === hr.rider.id);
                return (
                  <div style={{ marginTop: 8, borderTop: "1px solid var(--c-border)", paddingTop: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, fontFamily: "var(--font-mono, monospace)" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}></th>
                          {Array.from({ length: maxAttempts }, (_, a) => (
                            <th key={a} style={{ textAlign: "center", padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}>
                              A{a + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: competition.judge_count }, (_, j) => {
                          const judgeNum = j + 1;
                          const judge = members.find((m) => m.judge_number === judgeNum);
                          const name = judge?.judge_name ?? judge?.user?.display_name?.split(" ")[0] ?? `J${judgeNum}`;
                          return (
                            <tr key={j}>
                              <td style={{ padding: "2px 4px", color: "var(--c-text-muted)", fontWeight: 600 }}>{name}</td>
                              {Array.from({ length: maxAttempts }, (_, a) => {
                                const score = riderScores.find((s) => s.judge_number === judgeNum && s.attempt_number === a + 1);
                                return (
                                  <td key={a} style={{ textAlign: "center", padding: "2px 4px", color: score ? "var(--c-text)" : "var(--c-text-muted)" }}>
                                    {score ? score.value.toFixed(1) : "-"}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        <tr style={{ borderTop: "1px solid var(--c-border)" }}>
                          <td style={{ padding: "3px 4px", fontWeight: 700, color: "var(--c-text-muted)" }}>Final</td>
                          {Array.from({ length: maxAttempts }, (_, a) => {
                            const attemptScores = riderScores.filter((s) => s.attempt_number === a + 1);
                            const vals = attemptScores.map((s) => s.value);
                            const avg = vals.length > 0 ? vals.reduce((sum, v) => sum + v, 0) / vals.length : null;
                            return (
                              <td key={a} style={{ textAlign: "center", padding: "3px 4px", fontWeight: 700, color: avg != null ? "var(--c-accent)" : "var(--c-text-muted)" }}>
                                {avg != null ? avg.toFixed(1) : "-"}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                    {finals[hr.rider.id] != null && (
                      <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: "var(--c-accent)", fontFamily: "var(--font-mono, monospace)" }}>
                        Overall: {finals[hr.rider.id]!.toFixed(1)} ({competition.scoring_model})
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {heatRiders.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-title">No riders in this heat</div>
          <div className="empty-state-desc">
            Use <strong>Edit Heats</strong> to add riders.
          </div>
        </div>
      )}

      {/* Edit Heats modal */}
      {editHeatsOpen && competitionId && (
        <EditHeatsModal
          competitionId={competitionId}
          heats={heats}
          divisions={divisions}
          allRiders={riders}
          onClose={() => {
            setEditHeatsOpen(false);
            // Refresh current heat riders in case they changed
            const heatId = selectedHeat ?? liveState?.live_heat_id;
            if (heatId) {
              api
                .get<HeatRider[]>(`/api/competitions/${competitionId}/heats/${heatId}/riders`)
                .then(setHeatRiders);
            }
          }}
        />
      )}

      <NotificationToasts items={notifications} />

      {/* OBS Preview */}
      {competition.obs_token && <OBSPreview token={competition.obs_token} />}
    </div>
  );
}

const SMALL_SCALE = 0.15;
const LARGE_SCALE = 0.4;

function OBSPreview({ token }: { token: string }) {
  const [hovered, setHovered] = useState(false);
  const scale = hovered ? LARGE_SCALE : SMALL_SCALE;
  const w = 1920 * scale;
  const h = 1080 * scale;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: w,
        height: h,
        zIndex: 800,
        overflow: "hidden",
        borderRadius: 4,
        border: "1px solid var(--c-border)",
        background: "#000",
        transition: "width 0.25s ease, height 0.25s ease",
        cursor: "default",
      }}
    >
      <iframe
        src={`/obs/${token}`}
        style={{
          width: 1920,
          height: 1080,
          border: "none",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
        }}
        title="OBS Preview"
      />
    </div>
  );
}

function EditHeatsModal({
  competitionId,
  heats,
  divisions,
  allRiders,
  onClose,
}: {
  competitionId: string;
  heats: Heat[];
  divisions: Division[];
  allRiders: Rider[];
  onClose: () => void;
}) {
  const [selectedHeatId, setSelectedHeatId] = useState<string>(heats[0]?.id ?? "");
  const [heatRiders, setHeatRiders] = useState<HeatRider[]>([]);
  const [loadingRiders, setLoadingRiders] = useState(false);

  const selectedHeat = heats.find((h) => h.id === selectedHeatId);

  const loadRiders = useCallback(async (heatId: string) => {
    setLoadingRiders(true);
    const riders = await api.get<HeatRider[]>(
      `/api/competitions/${competitionId}/heats/${heatId}/riders`,
    );
    setHeatRiders(riders);
    setLoadingRiders(false);
  }, [competitionId]);

  useEffect(() => {
    if (selectedHeatId) loadRiders(selectedHeatId);
  }, [selectedHeatId, loadRiders]);

  const addRider = async (riderId: string) => {
    await api.post(`/api/competitions/${competitionId}/heats/${selectedHeatId}/riders/${riderId}`);
    loadRiders(selectedHeatId);
  };

  const removeRider = async (riderId: string, riderName: string) => {
    const ok = window.confirm(
      `Remove ${riderName} from ${selectedHeat?.name ?? "this heat"}?\n\nThis will also delete all their scores in this heat.`,
    );
    if (!ok) return;
    await api.delete(`/api/competitions/${competitionId}/heats/${selectedHeatId}/riders/${riderId}`);
    loadRiders(selectedHeatId);
  };

  const inHeatIds = new Set(heatRiders.map((hr) => hr.rider.id));
  const available = allRiders.filter((r) => !inHeatIds.has(r.id));

  // Group heats by division for the selector
  const divisionMap = new Map<string | null, Heat[]>();
  for (const heat of heats) {
    const key = heat.division_id;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(heat);
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          borderRadius: 8,
          width: "100%",
          maxWidth: 480,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--c-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15 }}>Edit Heats</div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--c-text-muted)",
              fontSize: 18,
              cursor: "pointer",
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>

        {/* Heat selector */}
        <div style={{ padding: "12px 20px 0" }}>
          <select
            value={selectedHeatId}
            onChange={(e) => setSelectedHeatId(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              background: "var(--c-surface-2)",
              border: "1px solid var(--c-border)",
              borderRadius: 6,
              color: "var(--c-text)",
              fontSize: 13,
              fontFamily: "var(--font-sans)",
            }}
          >
            {Array.from(divisionMap.entries()).map(([divisionId, divisionHeats]) => {
              const divName = divisionId
                ? divisions.find((d) => d.id === divisionId)?.name ?? "Division"
                : null;
              return divName ? (
                <optgroup key={divisionId ?? "__none"} label={divName}>
                  {divisionHeats.map((h) => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </optgroup>
              ) : (
                divisionHeats.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))
              );
            })}
          </select>
          <div
            className="text-sm text-muted"
            style={{ marginTop: 8, fontSize: 11, lineHeight: 1.4 }}
          >
            Removing a rider deletes all their scores in that heat.
          </div>
        </div>

        {/* Rider lists */}
        <div style={{ overflow: "auto", padding: "12px 20px 20px", flex: 1 }}>
          {loadingRiders ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              {heatRiders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    className="text-sm text-muted"
                    style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, fontWeight: 600 }}
                  >
                    In Heat ({heatRiders.length})
                  </div>
                  {heatRiders.map((hr) => (
                    <div
                      key={hr.rider.id}
                      className="flex items-center justify-between"
                      style={{ padding: "5px 0" }}
                    >
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{hr.rider.name}</span>
                        {hr.rider.nickname && (
                          <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                            {hr.rider.nickname}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 11, padding: "2px 8px", color: "var(--c-danger, #ef4444)" }}
                        onClick={() => removeRider(hr.rider.id, hr.rider.name)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {available.length > 0 && (
                <div>
                  <div
                    className="text-sm text-muted"
                    style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, fontWeight: 600 }}
                  >
                    Available ({available.length})
                  </div>
                  {available.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between"
                      style={{ padding: "5px 0" }}
                    >
                      <div>
                        <span style={{ fontSize: 13 }}>{r.name}</span>
                        {r.nickname && (
                          <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                            {r.nickname}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        onClick={() => addRider(r.id)}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {heatRiders.length === 0 && available.length === 0 && (
                <div className="text-sm text-muted" style={{ padding: "20px 0", textAlign: "center" }}>
                  No riders available. Add riders in Setup first.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
